import React, { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { caseStudies, personalProjects } from "../data/mock";
import Navbar from "../components/Navbar";
import CaseStudyHeroVisual from "../components/CaseStudyHeroVisual";
import {
  ProblemSection,
  ProcessSection,
  SketchesSection,
  DesignGoalsSection,
  MyApproachSection,
  DesignSystemSection,
  PullQuoteSection,
  FounderQuoteSection,
  OutcomeSection,
  LearningsSection,
  ExtraScreensSection,
  ProjectOverviewSection,
} from "../components/CaseStudySections";

const cursive = { fontFamily: "'Segoe Script', 'Caveat', cursive" };

// Build the list of "extra flow" sections from both the legacy single-flow fields
// (extraScreens*) and the newer extraFlows[] array.
const buildExtraFlows = (cs) => {
  const flows = [];
  if (cs.extraScreens && cs.extraScreens.length > 0) {
    flows.push({
      title: cs.extraScreensTitle,
      intro: cs.extraScreensIntro,
      flowLabel: cs.extraScreensFlowLabel,
      screensId: cs.extraScreensId,
      aspect: cs.extraScreensAspect || cs.screenAspect,
      screens: cs.extraScreens,
    });
  }
  if (Array.isArray(cs.extraFlows)) {
    cs.extraFlows.forEach((f) => flows.push(f));
  }
  return flows;
};

const CaseStudyNotFound = ({ navigate }) => (
  <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50 flex items-center justify-center px-6">
    <div className="text-center">
      <p className="text-[15px] text-neutral-500">Case study not found.</p>
      <button
        onClick={() => navigate("/")}
        className="mt-4 text-[14px] text-neutral-900 dark:text-neutral-50 underline underline-offset-4"
      >
        Back home
      </button>
    </div>
  </div>
);

const CaseStudyHeader = ({ cs, isPersonal, metaRows }) => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-16">
    <Link
      to="/#work"
      className="inline-flex items-center gap-1.5 text-[13.5px] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors duration-200"
    >
      <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.25} />
      All work
    </Link>

    <div className="mt-8 flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
      {isPersonal && (
        <>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 text-[10.5px] tracking-[0.18em]">
            Personal project
          </span>
          <span className="w-1 h-1 rounded-full bg-neutral-300 dark:bg-neutral-700" />
        </>
      )}
      <span>{cs.tag}</span>
      <span className="w-1 h-1 rounded-full bg-neutral-300 dark:bg-neutral-700" />
      <span>{cs.year}</span>
    </div>

    <h1 className="mt-4 text-[40px] sm:text-[56px] lg:text-[68px] leading-[1.04] tracking-[-0.035em] font-medium max-w-[1000px]">
      {cs.name}.{" "}
      <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">
        {cs.headline}
      </span>
    </h1>

    <p className="mt-7 text-[17px] sm:text-[19px] leading-[1.55] text-neutral-600 dark:text-neutral-400 max-w-[680px]">
      {cs.summary ? `${cs.summary} — ${cs.context}` : cs.context}
    </p>

    <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10 pt-8 border-t border-neutral-200 dark:border-neutral-800">
      {metaRows.map((m) => (
        <div key={m.k}>
          <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">{m.k}</div>
          <div className="mt-1.5 text-[14px] text-neutral-800 dark:text-neutral-200 leading-snug">{m.v}</div>
        </div>
      ))}
    </div>
  </section>
);

const CaseStudyPager = ({ prev, next }) => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-24 lg:pb-32">
    <div className="flex items-center justify-between gap-6 pt-10 border-t border-neutral-200 dark:border-neutral-800">
      <Link
        to={`/work/${prev.id}`}
        className="group flex items-center gap-2 text-[13.5px] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors duration-200"
      >
        <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-200 group-hover:-translate-x-0.5" strokeWidth={2.25} />
        <span>Previous · {prev.name}</span>
      </Link>
      <Link
        to={`/work/${next.id}`}
        className="group flex items-center gap-2 text-[14.5px] font-medium text-neutral-900 dark:text-neutral-50 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors duration-200"
      >
        <span className="text-right">
          <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
            Next case study
          </span>
          <span className="block">{next.name}</span>
        </span>
        <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.25} />
      </Link>
    </div>
  </section>
);

const CaseStudy = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Look up the project across both professional case studies and personal projects
  const professionalIdx = caseStudies.findIndex((c) => c.id === id);
  const personalIdx = personalProjects.findIndex((c) => c.id === id);
  const isPersonal = personalIdx !== -1;
  const list = isPersonal ? personalProjects : caseStudies;
  const idx = isPersonal ? personalIdx : professionalIdx;
  const cs = idx !== -1 ? list[idx] : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  const metaRows = useMemo(
    () =>
      [
        { k: "Role", v: cs?.role },
        { k: "Duration", v: cs?.duration },
        { k: "Team", v: cs?.team },
        { k: "Platform", v: cs?.platform },
      ].filter((m) => m.v),
    [cs?.role, cs?.duration, cs?.team, cs?.platform],
  );

  const extraFlows = useMemo(() => (cs ? buildExtraFlows(cs) : []), [cs]);

  if (!cs) return <CaseStudyNotFound navigate={navigate} />;

  const next = list[(idx + 1) % list.length];
  const prev = list[(idx - 1 + list.length) % list.length];
  const heroBeforeApproach = cs.heroPosition !== "after-approach" && !cs.hideHero;
  const heroAfterApproach = cs.heroPosition === "after-approach";

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50 selection:bg-neutral-900 selection:text-white dark:selection:bg-white dark:selection:text-neutral-900">
      <Navbar />

      <main className="pt-32 lg:pt-40">
        <CaseStudyHeader cs={cs} isPersonal={isPersonal} metaRows={metaRows} />

        {heroBeforeApproach && (
          <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-16 lg:pb-24">
            <CaseStudyHeroVisual cs={cs} />
          </section>
        )}

        {cs.projectOverview && <ProjectOverviewSection po={cs.projectOverview} />}

        <ProblemSection cs={cs} />

        {cs.designGoals && cs.designGoals.length > 0 && <DesignGoalsSection goals={cs.designGoals} />}

        {cs.myApproach && cs.myApproachPosition === "before-process" && (
          <MyApproachSection approach={cs.myApproach} />
        )}

        {heroAfterApproach && (
          <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-20 lg:pb-24">
            <CaseStudyHeroVisual cs={cs} />
          </section>
        )}

        <ProcessSection steps={cs.process} />

        {cs.sketches && cs.sketches.length > 0 && <SketchesSection sketches={cs.sketches} />}

        {extraFlows.map((f, i) => (
          <ExtraScreensSection
            key={f.screensId || f.title || `flow-${i}`}
            title={f.title}
            intro={f.intro}
            flowLabel={f.flowLabel}
            screens={f.screens}
            aspect={f.aspect || cs.screenAspect}
            id={`${cs.id}-flow-${i + 1}`}
            screensId={f.screensId}
          />
        ))}

        {cs.myApproach && cs.myApproachPosition !== "before-process" && (
          <MyApproachSection approach={cs.myApproach} />
        )}

        {cs.designSystem && <DesignSystemSection system={cs.designSystem} />}

        {cs.pullQuote && <PullQuoteSection quote={cs.pullQuote} />}

        {cs.founderQuote && <FounderQuoteSection q={cs.founderQuote} />}

        <OutcomeSection cs={cs} />

        <LearningsSection learnings={cs.learnings} />

        <CaseStudyPager prev={prev} next={next} />
      </main>
    </div>
  );
};

export default CaseStudy;
